import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import AuthLayout from "../components/AuthLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import RoleInfoModal from "../components/RoleInfoModal";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [infoRole, setInfoRole] = useState(null);
  const { login } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      login(data.token, data.user);
      toast.success(`Welcome back, ${data.user.name}`);
      navigate(data.user.role === "admin" ? "/lender" : "/dashboard");
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout>
        <div className="grid grid-cols-2 gap-3 mb-4 max-w-[300px] mx-auto w-full">
          <button
            type="button"
            onClick={() => setInfoRole("realtor")}
            data-testid="learn-more-realtor"
            className="group rounded-2xl overflow-hidden ring-1 ring-white/10 shadow-[0_12px_40px_-12px_rgba(255,59,48,0.5)] transition-transform hover:scale-[1.03] focus:outline-none focus:ring-2 focus:ring-[#ff3b30]"
            aria-label="Realtors — click here for more info"
          >
            <img src="/brand/realtor-cta.jpg" alt="Hey Realtors! Click here for more info" className="w-full h-auto block select-none" draggable="false" />
          </button>
          <button
            type="button"
            onClick={() => setInfoRole("lender")}
            data-testid="learn-more-lender"
            className="group rounded-2xl overflow-hidden ring-1 ring-white/10 shadow-[0_12px_40px_-12px_rgba(255,59,48,0.5)] transition-transform hover:scale-[1.03] focus:outline-none focus:ring-2 focus:ring-[#ff3b30]"
            aria-label="Lenders — click here for more info"
          >
            <img src="/brand/lender-cta.jpg" alt="Hey Lenders! Click here for more info" className="w-full h-auto block select-none" draggable="false" />
          </button>
        </div>

        <div className="bg-[#141414]/80 backdrop-blur-2xl border border-white/10 rounded-2xl p-6 shadow-2xl">
          <h1 className="font-display text-2xl tracking-tight">Lender / Realtor sign in</h1>
          <p className="text-white/50 text-sm mt-1.5">
            Issue instant pre-approvals and loan summaries for your clients. No calls needed.
          </p>

          <form onSubmit={submit} className="mt-5 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs tracking-[0.15em] uppercase text-white/50">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                className="bg-black/40 border-white/15 h-11 text-white focus:ring-2 focus:ring-[#ff3b30]"
                placeholder="you@brokerage.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs tracking-[0.15em] uppercase text-white/50">
                Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPw ? "text" : "password"}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password-input"
                  className="bg-black/40 border-white/15 h-11 text-white focus:ring-2 focus:ring-[#ff3b30] pr-11"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  data-testid="login-toggle-password"
                  aria-label={showPw ? "Hide password" : "Show password"}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white transition-colors"
                >
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {error && (
              <p className="text-sm text-[#ff3b30]" data-testid="login-error">
                {error}
              </p>
            )}

            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full h-11 bg-[#ff3b30] hover:bg-[#ff5147] text-white font-semibold rounded-xl transition-colors"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
            </Button>
          </form>

          <p className="text-sm text-white/50 mt-4 text-center">
            New agent?{" "}
            <Link to="/signup" className="text-[#ff3b30] hover:underline" data-testid="go-signup-link">
              Request access
            </Link>
          </p>
        </div>
        <RoleInfoModal role={infoRole} onClose={() => setInfoRole(null)} />
    </AuthLayout>
  );
}
